import {Inject, Injectable} from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';
import {WINDOW} from '../providers/window-provider';
import {UserOrganisationService} from '../organisation/user-organisation.service';
import {ActivatedRoute} from '@angular/router';
import {DevVersionService} from '../dev-versions/dev-version.service';
import {State} from '@ngrx/store';
import {KeycloakService} from '../authentication/keycloak.service';

class DispatchEntry {
  moduleName: string;
  uriPrefix: string;
  localhostPort: number;
}

const BASE_DEV_ENVIRONMENT_URL: string = 'https://o7apps-dev.outfit7.net';
const BASE_BACKEND_URL: string = 'https://apps.outfit7.com';
const BASE_O7PO_BACKEND_URL: string = 'https://po-stpceyl2ua-uc.a.run.app';
const CN_DEV_BACKEND_HOSTNAME: string = 'o7apps-dev.talkingtomandfriends.cn';
const CN_PROD_BACKEND_HOSTNAME: string = 'apps.talkingtomandfriends.cn';

const DISPATCH_ENTRIES: DispatchEntry[] = [
  {
    moduleName: 'processing',
    uriPrefix: '/rest/processing/',
    localhostPort: 8081
  },
  {
    uriPrefix: '/rest/ad/',
    localhostPort: 8082,
    moduleName: 'crosspromo'
  },
  {
    uriPrefix: '/rest/visual7/',
    localhostPort: 8089,
    moduleName: 'visual7-backend'
  },
  {
    uriPrefix: '/rest/admin/',
    localhostPort: 8092,
    moduleName: 'admin'
  },
  {
    uriPrefix: '/rest/apps/',
    localhostPort: 8091,
    moduleName: 'apps'
  },
  {
    uriPrefix: '/rest/ads/providers/',
    localhostPort: 8097,
    moduleName: 'ads'
  },
  {
    uriPrefix: '/rest/talkingFriends/v1/vca/',
    localhostPort: 8080,
    moduleName: 'default'
  },
  {
    uriPrefix: '/rest/talkingFriends/',
    localhostPort: 8096,
    moduleName: 'grid'
  },
  {
    uriPrefix: '/rest/compliance/',
    localhostPort: 8097,
    moduleName: 'compliance'
  },
  {
    uriPrefix: '',
    localhostPort: 8080,
    moduleName: 'default'
  }
];

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {

  private readonly EXCLUDED_URLS: string[] = ['/assets', '/clients/public'];

  constructor(@Inject(WINDOW) private window: Window,
              private route: ActivatedRoute,
              private userOrganisationService: UserOrganisationService,
              // Store state enables to return values directly from the store without using observables
              private state: State<any>,
              private keycloakService: KeycloakService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isExcluded: boolean = this.EXCLUDED_URLS.some(url => req.url.includes(url));

    if (isExcluded) {
      return next.handle(req);
    }

    // Skip converting absolute urls
    if (/^https?:\/\//i.test(req.url)) {
      return next.handle(req.clone({
        setHeaders: {'Authorization': `Bearer ${this.keycloakService.getKeycloakInstance()?.token}`}
      }));
    } else {
      return next.handle(req.clone({
        url: this.getDispatchUrl(req.url, this.state.getValue().dashboardUser.userOrganisation),
        params: req.params,
        setHeaders: {'Authorization': `Bearer ${this.keycloakService.getKeycloakInstance()?.token}`}
      }));
    }
  }

  private getDispatchUrl(url: string, selectedOrganisationId: string): string {

    if (url.endsWith('.html')) {
      return url;
    } else if (url.startsWith('/placement/optimization')) {
      return this.getPlacementOptimizationServiceBackendUrl(url);
    } else if (url.startsWith('/o7devices/')) {
      return this.getVisual7BackendUrl(url);
    }else {
      return this.getBaseBackendUrl(url, selectedOrganisationId);
    }
  }

  private getPlacementOptimizationServiceBackendUrl(url: string): string {
    return BASE_O7PO_BACKEND_URL + url;
  }

  private getVisual7BackendUrl(url: string): string {
    const selectedRegion: string = this.state.getValue().applicationState.region;
    const useDevEnvironment: string = this.state.getValue().applicationState.useDevEnvironment;

    const environmentUrlPart: string = useDevEnvironment ? 'be-dev.visual7.app' : 'be.visual7.app';
    const region: string = selectedRegion === DevVersionService.CN_REGION ? DevVersionService.CN_REGION : DevVersionService.GLOBAL_REGION;

    return 'https://' + environmentUrlPart + url + '?region=' + region;
  }

  private getBaseBackendUrl(url: string, selectedOrganisationId: string): string {
    const selectedBeDevVersion: string = this.state.getValue().applicationState.beDevVersion;
    const selectedRegion: string = this.state.getValue().applicationState.region;
    const useDevEnvironment: string = this.state.getValue().applicationState.useDevEnvironment;

    if (selectedBeDevVersion && selectedBeDevVersion != DevVersionService.PROD_VERSION) {
      // Dev version is selected
      let devVersionsUrlPart: string = 'https://' + selectedBeDevVersion + '.';
      let environmentUrlPart: string = useDevEnvironment ? 'o7apps-dev.outfit7.net' : 'apps.outfit7.com';
      if (selectedRegion === DevVersionService.CN_REGION) {
        return devVersionsUrlPart + CN_DEV_BACKEND_HOSTNAME + url;
      }
      return devVersionsUrlPart + environmentUrlPart + url;
    } else {
      // Use prod version
      if (!useDevEnvironment) {
        if (selectedRegion === DevVersionService.CN_REGION) {
          return  'https://' + CN_PROD_BACKEND_HOSTNAME + url;
        }
        return BASE_BACKEND_URL + url;
      } else {
        if (selectedRegion === DevVersionService.CN_REGION) {
          return  'https://' + CN_DEV_BACKEND_HOSTNAME + url;
        }
        // Use dev environment on production version backend
        return BASE_DEV_ENVIRONMENT_URL + url;
      }
    }
  }

  private getMatchingDispatchEntry(relativeUrl: string): DispatchEntry {
    const dispatchEntry: DispatchEntry = DISPATCH_ENTRIES.find((entry) => relativeUrl.startsWith(entry.uriPrefix));
    return dispatchEntry ? dispatchEntry : DISPATCH_ENTRIES[DISPATCH_ENTRIES.length - 1];
  }

}
